import React from 'react'
import { Element, Button, Modal, Heading } from 'react-bulma-components'
import { Link } from 'react-router-dom'
import { addDays, startOfDay, subDays, isSameDay } from 'date-fns'
import { upperFirst } from 'lodash'
import { Clocking } from '../../api/clockings'
import { WorkPeriod } from '../../api/work-periods'
import ClockingsTimeline from './clockings-timeline'
import { useCreateWorkPeriodMutation } from '../../queries/work-periods'
import { queryClient } from '../../queries'
import { getDocIdAsString } from '../../utils/documents'
import { formatCompleteDate, dateToDateInputValue } from '../../utils/date'
import { useNavigate } from 'react-router-dom'
import { useClockingsQuery, useClockingQuery } from '../../queries/clockings'
import AddWorkPeriodForm from '../work-periods/forms/add-work-period-form'
import ConfirmationModal from '../confirmation-modal/confirmation-modal'

interface Props {
  show: boolean
  hide: () => void
  triggerClocking: Clocking
}

const CreateWorkPeriodFromClockingsModal: React.FC<Props> = ({ show, hide, triggerClocking }) => {
  const navigate = useNavigate()
  const createMutation = useCreateWorkPeriodMutation()
  const mission = triggerClocking.matchingMission!
  const triggerDate = new Date(triggerClocking.date)
  const clockingQuery = useClockingQuery(triggerClocking._id, { enabled: show })
  const candidateWorkPeriods = clockingQuery.data?.candidateWorkPeriods || []
  // Day selected for "Ajouter" sub-modal — null means closed
  const [addingDay, setAddingDay] = React.useState<Date | null>(null)

  // Fetch sibling orphaned clockings for day-1/day/day+1
  const orphanClockingsQuery = useClockingsQuery(
    {
      user: [getDocIdAsString(triggerClocking.user)],
      start: startOfDay(subDays(triggerDate, 1)),
      end: startOfDay(addDays(triggerDate, 2)),
      type: 'null',
      mission: 'null',
    },
    { enabled: show },
  )
  const orphanClockings = (orphanClockingsQuery.data || []).sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  )

  const handleSubmit = (payload: { start: { date: Date }; end: { date: Date } }) => {
    createMutation.mutate(
      {
        missionId: getDocIdAsString(mission),
        start: payload.start,
        end: payload.end,
      },
      {
        onSuccess: data => {
          queryClient.invalidateQueries(['clockings'])
          setAddingDay(null)
          hide()
          navigate(
            `/missions/${getDocIdAsString(data.mission)}/work-periods/${getDocIdAsString(data.workPeriod)}`,
          )
        },
      },
    )
  }

  // Build the 3 days: day-1, day, day+1
  const dayIds: string[] = ['prev-day', 'this-day', 'next-day']
  const days = [-1, 0, 1].map((offset, i) => {
    const day = addDays(startOfDay(triggerDate), offset)
    const wp = candidateWorkPeriods.find(w => isSameDay(new Date(w.start.date), day))
    const clockingsForDay = orphanClockings.filter(c => isSameDay(new Date(c.date), day))
    const isToday = offset === 0
    const testId = dayIds[i]
    return { day, wp, clockingsForDay, isToday, testId }
  })

  return (
    <>
      {/* Main context modal */}
      <Modal show={show && !addingDay} onClose={hide} closeOnEsc closeOnBlur>
        <Modal.Card data-test="create-wp-from-clockings-modal">
          <Modal.Card.Header>
            <Modal.Card.Title>Pointage orphelin</Modal.Card.Title>
          </Modal.Card.Header>
          <Modal.Card.Body>
            <Heading size={5} mb={4}>
              {triggerClocking.user &&
                `${(triggerClocking.user as any).firstName} ${(triggerClocking.user as any).lastName}`}
            </Heading>

            {days.map(({ day, wp, clockingsForDay, isToday, testId }) => (
              <DaySection
                key={day.toISOString()}
                day={day}
                wp={wp}
                clockingsForDay={clockingsForDay}
                isToday={isToday}
                testId={testId}
                isMissionValidated={!!mission.isValidated}
                onClickAjouter={() => setAddingDay(day)}
                onCorrect={hide}
              />
            ))}
          </Modal.Card.Body>
          <Modal.Card.Footer>
            <Button onClick={hide}>Fermer</Button>
          </Modal.Card.Footer>
        </Modal.Card>
      </Modal>

      {/* Ajouter sub-modal */}
      {addingDay && (
        <AddWorkPeriodForm
          mission={mission}
          onSubmit={handleSubmit}
          mutation={createMutation}
          formKey={`add-${addingDay.toISOString()}`}
          day={dateToDateInputValue(addingDay)}
          userId={getDocIdAsString(triggerClocking.user)}
          workPeriodMatchRange={mission.jobTitle?.clockingRule?.workPeriodMatchRange}
        >
          {({ handleSubmit: formHandleSubmit, content }) => (
            <ConfirmationModal
              show={!!addingDay}
              onCancel={() => setAddingDay(null)}
              onConfirm={() => formHandleSubmit()}
              confirmButtonLabel="Créer la journée"
              cancelButtonLabel="Retour"
              confirmButtonProps={{
                disabled: createMutation.isLoading,
              }}
              title={`Ajouter un horaire — ${upperFirst(formatCompleteDate(addingDay))}`}
            >
              {content}
            </ConfirmationModal>
          )}
        </AddWorkPeriodForm>
      )}
    </>
  )
}

// --- DaySection ---

interface DaySectionProps {
  day: Date
  wp?: WorkPeriod
  clockingsForDay: Clocking[]
  isToday: boolean
  testId: string
  isMissionValidated: boolean
  onClickAjouter: () => void
  onCorrect: () => void
}

const DaySection: React.FC<DaySectionProps> = ({
  day,
  wp,
  clockingsForDay,
  isToday,
  testId,
  isMissionValidated,
  onClickAjouter,
  onCorrect,
}) => {
  const wpUrl = wp
    ? `/missions/${getDocIdAsString(wp.mission)}/work-periods/${getDocIdAsString(wp)}`
    : null

  return (
    <Element
      data-test={testId}
      mb={3}
      p={3}
      style={{
        borderLeft: isToday ? '3px solid #3273dc' : '3px solid #eee',
        background: isToday ? '#f5f8ff' : undefined,
        borderRadius: '4px',
      }}
    >
      {/* Day header + action */}
      <Element display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Element renderAs="strong">{upperFirst(formatCompleteDate(day))}</Element>
        {wp && wpUrl && !wp.isValidated && (
          <Button
            size="small"
            color="primary"
            outlined
            renderAs={Link}
            to={wpUrl}
            onClick={onCorrect}
          >
            Corriger l'horaire
          </Button>
        )}
        {!wp && !isMissionValidated && (
          <Button size="small" color="primary" outlined onClick={onClickAjouter}>
            Ajouter un horaire
          </Button>
        )}
      </Element>

      {/* Chronological timeline */}
      <Element ml={3}>
        <ClockingsTimeline clockings={clockingsForDay} workPeriod={wp} />
      </Element>
    </Element>
  )
}

export default CreateWorkPeriodFromClockingsModal
